import React, { useEffect, useState } from 'react';
import { Button, Card, Input, InputNumber, Select, Space, Typography } from 'antd';
import PropTypes from 'prop-types';

import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

const R_LEVEL_OPTIONS = [
    { value: 1, label: '1R' },
    { value: 2, label: '2R' },
    { value: 3, label: '3R' },
    { value: 4, label: '4R' },
    { value: 5, label: '5R' },
    { value: 6, label: '6R' }
];

export default function SimulationPlanCard({ plan, onUpdate }) {
    const [localPartials, setLocalPartials] = useState(plan.partials);
    const [planName, setPlanName] = useState(plan.planName);

    // Calculate total percentage
    const totalPercentage = localPartials.reduce((sum, p) => sum + (p.percentage || 0), 0);
    const isValid = totalPercentage === 100;

    // Update parent when partials change
    useEffect(() => {
        if (isValid) {
            onUpdate({ partials: localPartials, planName });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
    }, [localPartials, planName, isValid]);

    const addPartial = () => {
        if (localPartials.length >= 6) return;
        setLocalPartials([...localPartials, { rLevel: 1, percentage: 0 }]);
    };

    const removePartial = (index) => {
        if (localPartials.length <= 1) return;
        setLocalPartials(localPartials.filter((_, i) => i !== index));
    };

    const updatePartial = (index, field, value) => {
        const newPartials = [...localPartials];
        newPartials[index] = { ...newPartials[index], [field]: value };
        setLocalPartials(newPartials);
    };

    return (
        <Card
            className={`simulation-plan-card ${!isValid ? 'invalid' : ''}`}
            size="small"
            title={
                <Input
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    bordered={false}
                    style={{ fontWeight: 'bold' }}
                />
            }
        >
            <div className="partials-list">
                {localPartials.map((partial, index) => (
                    // eslint-disable-next-line react/no-array-index-key -- Reason: legacy suppression retained until the owning code is migrated or cleaned up.
                    <div key={index} className="partial-row">
                        <Space>
                            <Text>Take</Text>
                            <InputNumber
                                min={0}
                                max={100}
                                value={partial.percentage}
                                onChange={(val) => updatePartial(index, 'percentage', val)}
                                style={{ width: 60 }}
                            />
                            <Text>% at</Text>
                            <Select
                                value={partial.rLevel}
                                onChange={(val) => updatePartial(index, 'rLevel', val)}
                                options={R_LEVEL_OPTIONS}
                                style={{ width: 70 }}
                            />
                            {localPartials.length > 1 && (
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removePartial(index)}
                                    size="small"
                                />
                            )}
                        </Space>
                    </div>
                ))}
            </div>

            <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addPartial}
                size="small"
                disabled={localPartials.length >= 6}
                style={{ marginTop: 8 }}
                block
            >
                Add Partial
            </Button>

            <div className="total-percentage" style={{ marginTop: 8 }}>
                <Text type={isValid ? 'success' : 'danger'}>Total: {totalPercentage}%</Text>
                {!isValid && (
                    <Text type="danger" style={{ display: 'block', fontSize: 12 }}>
                        Must equal 100%
                    </Text>
                )}
            </div>
        </Card>
    );
}

SimulationPlanCard.propTypes = {
    plan: PropTypes.shape({
        planName: PropTypes.string.isRequired,
        partials: PropTypes.arrayOf(
            PropTypes.shape({
                rLevel: PropTypes.number.isRequired,
                percentage: PropTypes.number.isRequired
            })
        ).isRequired
    }).isRequired,
    onUpdate: PropTypes.func.isRequired
};
